import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { Formik, useField } from "formik";
import {
  $Button,
  $Input,
  $Search,
  $Skeleton,
  $TableTree,
  $Checkbox,
} from "../antd";
import { PostalAreaDetails } from "./Types";
import { useTranslation } from "react-i18next";
import { PlusOutlined, MoreOutlined } from "us.icons";
import * as Action from "us.common/actions";
import { IOnFilter, IOnSort } from "us.common/components/antd/TableTree";
import { getPostalCodes, getRemovePostalCode } from "us.common/functions";
import _ from "lodash";
import "./Style.scss";
import { $Popover } from "us.common/components";
import { ItemMenu } from "./Component/ItemMenu";
import { IItemMenu } from "./Interfaces";
import { PostalCodesActions } from "us.common/components/EntitySelection/Component";
import { ITEM_MENU_DEFAULT_STATE, PostalAreasColumns } from "./constants";
import { getCountryFilters, clearText } from "./Functions";
interface ISortOrder {
  columnKey: string;
  order: any;
}

interface IPostalAreaFormat {
  id: any;
  postalPlace: any;
  postalCode: any;
  countyCode: any;
  countyName: any;
  key: any;
}

interface ISelectPostalArea {
  drawerContentChangeClick: (data: PostalAreaDetails) => void;
  currentStep?: any;
  currentInfo?: any;
  countries: any;
  cancelClick: (currentValues: any) => void;
  onClickAddPostalArea: () => void;
  updateFormData: (data: any) => void;
  updateDrawerName: (data: string) => void;
  clearPostalData: any;
  resetExisitingPostalCode: (data: any) => void;
}

interface IBase extends ISelectPostalArea {
  name?: string;
  multiple?: boolean;
  postalAreas: any[];
  getPostalAreas?: any;
  isLoading: boolean;
  isPostalAreasLoading?: boolean;
  countryCode?: string;
  selectedPostalCodes?: Array<any>;
  postalAreaClose?: () => void;
  onSelect?: (selectedKeys: Array<any>) => void;
  onSubmitSelection?: (selectedKeys: Array<any>) => void;
  isDrawer?: boolean;
  onCallAction?: any;
  systemCountryCode?: string;
}

const SelectPostalArea: React.FC<IBase> = (props) => {
  const { t } = useTranslation(["US.COLLECTION.DEBTOR"], { useSuspense: true });
  const {
    name = "selectedPostalCodes",
    postalAreas,
    getPostalAreas,
    isLoading,
    isPostalAreasLoading,
    clearPostalData,
    countryCode,
    countries,
    multiple = false,
    selectedPostalCodes = [],
    onSelect,
    postalAreaClose,
    onSubmitSelection,
    resetExisitingPostalCode,
    isDrawer = true,
    onCallAction,
    systemCountryCode,
  } = props;

  const [, , helpers] = useField(name);

  const [sortedInfo, setSortedInfo] = useState<ISortOrder>({
    columnKey: "",
    order: "",
  });
  const [selectedRowKey] = useState([]);

  const [searchText, setSearchText] = useState<string>("");

  const [data, setData] = useState<IPostalAreaFormat[]>([]);
  const [dataBkp, setDataBkp] = useState<IPostalAreaFormat[]>([]);
  const [popVisible, setPopVisible] = useState<number>(-1);
  const [itemMenu, setItemMenu] = useState<IItemMenu>(ITEM_MENU_DEFAULT_STATE);

  const { updateFormData, updateDrawerName } = props;

  useEffect(() => {
    clearPostalData({});
    updateDrawerName(
      t("US.COLLECTION.COMMON:ENTITYSELECTION.SELECTPOSTALAREA")
    );
  }, []);

  useEffect(() => {
    setData([...postalAreas]);
    setDataBkp([...postalAreas]);
  }, [postalAreas]);

  // auto close client account item menu after 5 seconds
  const setAutoClose = (id: number) => {
    setTimeout(() => {
      setItemMenu(ITEM_MENU_DEFAULT_STATE);
    }, 5000);
  };
  const postalFilters = {
    countryCode: countryCode ?? systemCountryCode,
    postalCode: "",
    searchType: "all",
  };

  const column: any = [
    {
      title: "",
      key: "more",
      dataIndex: "more",
      width: 50,
      customRenderChild: (text: any, record: any, index: number) => {
        const { followupType } = record;
        return (
          <$Popover
            placement="bottomLeft"
            trigger="click"
            open={itemMenu.visible && itemMenu.id == record.postalCodeId}
            onOpenChange={(visible: boolean) => {
              setItemMenu({
                ...itemMenu,
                visible,
                id: record.postalCodeId,
              });
              setAutoClose(record.postalCodeId);
            }}
            content={
              <ItemMenu
                onCallAction={(actionType: any) => {
                  setItemMenu(ITEM_MENU_DEFAULT_STATE);
                  onCallAction(actionType, record);
                }}
                isUsed={record.isUsed}
              />
            }
            destroyTooltipOnHide
          >
            <$Button
              id="btnItemMenu"
              type="default"
              data-testid="popover-btn"
              icon={<MoreOutlined />}
              size="small"
            />
          </$Popover>
        );
      },
    },
    {
      title: t("US.COLLECTION.COMMON:ENTITYSELECTION.POSTALCODE"),
      dataIndex: "postalCode",
      key: "postalCode",
      className: "text-nowrap",
      customSorter: (a: any, b: any) => a - b,
      customFilter: true,
      customRenderChild: (text: any, record: any, index: number) => {
        const { postalCode } = record;
        return <div>{postalCode}</div>;
      },
    },
    {
      title: t("US.COLLECTION.COMMON:ENTITYSELECTION.POSTALAREA"),
      //title: t('US.COMMON:COMMON.POSTAL_PLACE'),
      dataIndex: "postalPlace",
      key: "postalPlace",
      className: "text-nowrap",
      customSorter: (a: any, b: any) => a.localeCompare(b),
      customFilter: true,
    },
    {
      title: t("US.COLLECTION.COMMON:ENTITYSELECTION.MUNICIPALITYCODE"),
      // title: t('US.COMMON:COMMON.MUNICIPALITY_CODE'),
      dataIndex: "countyCode",
      key: "countyCode",
      className: "text-nowrap",
      customSorter: (a: any, b: any) => a - b,
      customFilter: true,
    },
    {
      title: t("US.COLLECTION.COMMON:ENTITYSELECTION.MUNICIPALITYNAME"),
      //title: t('US.COMMON:COMMON.MUNICIPALITY_NAME'),
      dataIndex: "countyName",
      key: "countyName",
      className: "text-nowrap",
      customSorter: (a: any, b: any) => a.localeCompare(b),
      customFilter: true,
    },
    {
      title: t("US.COLLECTION.COMMON:ENTITYSELECTION.COUNTRY"),
      dataIndex: PostalAreasColumns.COUNTRY,
      key: PostalAreasColumns.COUNTRY,
      className: "text-nowrap",
      customSorter: (a: any, b: any) => a.localeCompare(b),
      filters: getCountryFilters(countries?.data, PostalAreasColumns.COUNTRY),
      filterSearch: true,
      onFilter: (value: string, { country }: any) =>
        clearText(country) == clearText(value),
    },
    {
      title: t("US.COLLECTION.COMMON:ENTITYSELECTION.STANDARD_CODE"),
      dataIndex: PostalAreasColumns.COUNTRY_CODE,
      key: PostalAreasColumns.COUNTRY_CODE,
      className: "text-nowrap",
      customSorter: (a: any, b: any) => a.localeCompare(b),
      filters: getCountryFilters(
        countries?.data,
        PostalAreasColumns.COUNTRY_CODE
      ),
      filterSearch: true,
      onFilter: (value: string, { countryCode }: any) =>
        clearText(countryCode) == clearText(value),
    },
  ];

  const handleChange = (
    pagination: any,
    filters: Partial<
      Record<
        | "postalPlace"
        | "PostalCode"
        | "countyCode"
        | "countyName"
        | "id"
        | "key",
        string[]
      >
    >,
    sorter: any
  ) => {
    setSortedInfo(sorter);
  };

  const rowDoubleClickHandler = (dataRow: IPostalAreaFormat, rowIndex: any) => {
    if (isDrawer) {
      dataRow = dataRow || selectedRowKey;
      let newInfo = {
        ...props.currentInfo,
        PostalCode: dataRow.postalCode,
        City: dataRow.postalPlace,
        MunicipalityCode: dataRow.countyCode,
        MunicipalityName: dataRow.countyName,
      };
      delete newInfo.id;
      props.drawerContentChangeClick({
        currentStep: props.currentStep,
        updatedInfo: newInfo,
      });
    } else {
      onCallAction(PostalCodesActions.EDIT, dataRow);
    }
  };

  const handleSort: IOnSort = (sortData, dataSource) => {
    return sortData(dataSource);
  };

  const handleFilter: IOnFilter = (searchData, dataSource) => {
    return searchData(dataSource);
  };

  const handleSelection = (rowKeys: Array<any>) => {
    helpers.setValue(rowKeys);
    onSubmitSelection && onSubmitSelection(rowKeys);
    postalAreaClose && postalAreaClose();
  };

  return (
    <Formik
      initialValues={{
        postalCodes: "",
      }}
      enableReinitialize
      validateOnChange
      validateOnBlur
      onSubmit={() => {}}
    >
      {({ values, ...restProps }: any) => (
        <div className="select-postal-area">
          {isDrawer && (
            <div className="d-flex mb-4">
              <$Search
                size="small"
                name="postalCodes"
                placeholder={t("US.COLLECTION.COMMON:COMMON.SEARCH")}
                onSearch={(e: any) => {
                  getPostalAreas({
                    ...postalFilters,
                    searchText: e === "" ? "" : e,
                  });
                }}
                onPressEnter={(e: any) =>
                  getPostalAreas({
                    ...postalFilters,
                    searchText: e.target.value === "" ? "" : e.target.value,
                  })
                }
                allowClear={true}
              />
              <$Button
                type="dashed"
                size="small"
                icon={<PlusOutlined />}
                className="ml-2"
                onClick={() => {
                  props.onClickAddPostalArea();
                  updateFormData({});
                  updateDrawerName(
                    t("US.COLLECTION.COMMON:ENTITYSELECTION.NEW_POSTAL_AREA")
                  );
                  resetExisitingPostalCode({});
                }}
              >
                {t("US.COLLECTION.COMMON:ENTITYSELECTION.NEW_AREA")}
              </$Button>
            </div>
          )}
          <$Skeleton
            loading={isLoading || isPostalAreasLoading}
            active
            paragraph={{ rows: 3 }}
          >
            <$TableTree
              data-testid="postal-area-table"
              onChange={handleChange}
              rowKey={(record: any) => record.postalCodeId}
              data={data}
              size="small"
              className={!isDrawer ? "mt-3" : ""}
              onSort={handleSort}
              onFilter={handleFilter}
              virtual={true}
              filterOnType={true}
              resetOnSourceChange={true}
              bordered
              pagination={{ defaultPageSize: 20 }}
              locale={{
                filterConfirm: t("COMMON.OK"),
                filterReset: t("COMMON.RESET"),
              }}
              columns={
                isDrawer ? [column[1], column[2], column[3], column[4]] : column
              }
              onRow={(record: any, rowIndex: any) => {
                return {
                  onDoubleClick: (event: any) => {
                    if (!multiple) {
                      rowDoubleClickHandler(record, rowIndex);
                    }
                  },
                };
              }}
              firstColSkipFilterProps={-1}
              scroll={{
                x: "100%",
                y: !isDrawer ? "calc(100vh - 165px)" : "calc(100vh - 245px)",
              }}
              rowSelection={
                multiple
                  ? {
                      type: "checkbox",
                      hideSelectAll: true,
                      columnTitle: (
                        <$Checkbox
                          name="isAllCreditors"
                          checked={
                            data?.length == 0
                              ? false
                              : _.isEqual(
                                  getPostalCodes(data),
                                  getPostalCodes(selectedPostalCodes)
                                )
                          }
                          onChange={() => {
                            if (onSelect) {
                              selectedPostalCodes?.length !== data?.length
                                ? onSelect(data)
                                : onSelect([]);
                            }
                          }}
                        />
                      ),
                      onChange: (selectedRowKeys: Array<any>) => {
                        if (
                          selectedRowKeys.length < selectedPostalCodes.length
                        ) {
                          onSelect &&
                            onSelect(
                              selectedPostalCodes.filter(
                                (s: any) =>
                                  s.postalCode !=
                                  getRemovePostalCode(
                                    selectedPostalCodes,
                                    selectedRowKeys
                                  )
                              )
                            );
                        } else {
                          selectedRowKeys.map((p: any) => {
                            if (
                              !getPostalCodes(selectedPostalCodes).includes(p)
                            ) {
                              data.map((d: any) => {
                                if (d.postalCode === p) {
                                  onSelect &&
                                    onSelect([
                                      ...selectedPostalCodes,
                                      {
                                        postalCode: d.postalCode,
                                        postalPlace: d.postalPlace,
                                      },
                                    ]);
                                }
                              });
                            }
                          });
                        }
                      },
                      preserveSelectedRowKeys: true,
                      selectedRowKeys: getPostalCodes(selectedPostalCodes),
                    }
                  : undefined
              }
            />
          </$Skeleton>
          {multiple && (
            <div className="drawer-footer-fixed align-items-end justify-content-between">
              <div className="d-flex flex-column">
                <div className="font-weight-bold">
                  {isLoading ? 0 : selectedPostalCodes?.length}
                </div>
                <div>
                  {t(
                    "US.COLLECTION.COMMON:ENTITYSELECTION.SELECTED_POSTAL_CODES"
                  )}
                </div>
              </div>

              <div>
                <$Button
                  className="ml-3 mr-2"
                  data-testid="confirm-selection"
                  type="primary"
                  disabled={isLoading || selectedPostalCodes?.length === 0}
                  onClick={() => handleSelection(selectedPostalCodes)}
                >
                  {t("US.COLLECTION.COMMON:COMMON.SELECT")}
                </$Button>
                <$Button
                  onClick={() => postalAreaClose && postalAreaClose()}
                  data-testid="cancel-selection"
                >
                  {t("US.COLLECTION.COMMON:COMMON.CANCEL")}
                </$Button>
              </div>
            </div>
          )}
        </div>
      )}
    </Formik>
  );
};

const mapStateToProps = (state: any) => {
  const { entityDetails, common } = state;
  const { countries, postalAreas } = entityDetails;
  const { systemCountryCode } = common;
  return {
    postalAreas,
    countries,
    isLoading: state.entity.isLoading,
    systemCountryCode,
  };
};

const mapDispatchToProps = {
  getPostalAreas: Action.entity.getPostalAreas,
  updateFormData: Action.entity.PostalCodeFormData,
  updateDrawerName: Action.entity.ChangePostalDrawerTitle,
  clearPostalData: Action.entity.clearPostalData,
  resetExisitingPostalCode: Action.entity.postalCodeAvailability.reset,
};

export default connect(mapStateToProps, mapDispatchToProps)(SelectPostalArea);
